// src/pages/auth/ForgotPassword.jsx
import React, { useEffect, useState } from 'react';
import { auth } from '../../firebaseConfig';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  Image,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

/** Simple client-side abuse controls (per-browser)
 *  - COOLDOWN_MS: disable the button briefly after any attempt
 *  - MAX_PER_WINDOW / WINDOW_MS: cap number of attempts per rolling window
 */
const COOLDOWN_MS = 8000;           // 8 seconds between clicks
const WINDOW_MS = 15 * 60 * 1000;   // 15 minutes
const MAX_PER_WINDOW = 3;           // 3 attempts per 15 minutes
const LS_KEY = 'fh_fp_attempts_v1'; // localStorage key

function getRecentAttempts() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const arr = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
    const now = Date.now();
    return arr.filter((t) => typeof t === 'number' && now - t < WINDOW_MS);
  } catch {
    return [];
  }
}

function pushAttempt() {
  const arr = getRecentAttempts();
  arr.push(Date.now());
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(arr));
  } catch {
    // ignore storage errors
  }
}

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Cooldown state
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  // Honeypot field (bots often fill this)
  const [website, setWebsite] = useState('');

  const toast = useToast();

  // Tick down the cooldown timer
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = () => {
      const delta = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
      setSecondsLeft(delta);
      if (delta === 0) {
        setCooldownUntil(0);
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const startCooldown = (ms = COOLDOWN_MS) => {
    setCooldownUntil(Date.now() + ms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // If honeypot was filled → pretend success, do nothing
    if (website && website.trim().length > 0) {
      startCooldown();
      toast({
        title: 'Password reset email sent',
        description: 'Check your inbox for instructions to reset your password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Basic email validation + length cap to avoid junk input
    const addr = email.trim();
    const isEmailish = /^\S+@\S+\.\S+$/.test(addr);
    if (!isEmailish || addr.length > 254) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Local per-browser throttle
    const recent = getRecentAttempts();
    if (recent.length >= MAX_PER_WINDOW) {
      startCooldown(15000); // 15s UI pause when local quota exceeded
      toast({
        title: 'Too many attempts',
        description: 'Please wait a few minutes and try again.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      startCooldown();       // short pause regardless of outcome
      pushAttempt();         // record locally

      await sendPasswordResetEmail(auth, addr, {
        url: window.location.origin + '/login', // return to Login after reset
        handleCodeInApp: true,
      });

      toast({
        title: 'Password reset email sent',
        description: 'Check your inbox for instructions to reset your password.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('ForgotPassword error:', error);

      // If Firebase flags abuse, extend the cooldown and show a neutral message
      if (error?.code === 'auth/too-many-requests') {
        startCooldown(30000); // 30s UI pause on server-side rate limit
        toast({
          title: 'Please wait',
          description: 'Too many requests. Try again shortly.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Neutral error to avoid user enumeration
        toast({
          title: 'Error',
          description:
            'Could not send reset email. Please double-check the address or try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || cooldownUntil > Date.now();

  return (
    <Center minH="100vh" bg="gray.50">
      <Box
        p={8}
        maxW="md"
        w="full"
        borderWidth={1}
        borderRadius="lg"
        boxShadow="lg"
        bg="white"
      >
        <Image src="/images/logo.png" alt="FixiHub Logo" maxW="240px" mb={6} mx="auto" />

        <VStack spacing={6}>
          <Heading size="lg" textAlign="center">
            Forgot Password?
          </Heading>
          <Text color="gray.600" textAlign="center">
            Enter your email to receive a password reset link.
          </Text>

          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <VStack spacing={4}>
              {/* Honeypot field (hidden). Bots often fill inputs named 'website' */}
              <input
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-5000px',
                  top: 'auto',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              />

              <FormControl id="email">
                <FormLabel>Email address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  maxLength={254}
                  autoComplete="email"
                  spellCheck={false}
                />
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                width="full"
                isLoading={loading}
                isDisabled={disabled}
                size="lg"
              >
                {cooldownUntil > Date.now() && secondsLeft > 0
                  ? `Please wait… ${secondsLeft}s`
                  : 'Send Reset Email'}
              </Button>
            </VStack>
          </form>

          <Text textAlign="center">
            <RouterLink
              to="/login"
              style={{ color: 'var(--chakra-colors-blue-500)', fontWeight: 'bold' }}
            >
              Back to login
            </RouterLink>
          </Text>
        </VStack>
      </Box>
    </Center>
  );
}

export default ForgotPassword;
